import {
  buildProductMaterialCharacteristics,
  type DeepPartial,
  getCompleteItemCharacteristics,
  hasDistinctItemCharacteristics,
  isToothScopedItem,
  type ItemMaterialCharacteristics,
  ItemScopeEnum,
  type ItemType,
  type MaterialCharacteristics,
  materialCharacteristicsSchema,
  type ProductMaterialCharacteristics,
  productMaterialCharacteristicsSchema,
  type ProductType,
  SOLIDARIZED_PRODUCT_TYPE,
} from "@jasper/client-models";
import {
  ProductMaterialCharacteristicsForm,
  validateSchema,
  type ValidationErrors,
} from "@jasper/forms";
import {
  useItemMaterialsByItemType,
  type UpdateProductArgs,
} from "@jasper/use-cases";
import { Alert, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useStepBulkGroup } from "../hooks/useStepBulkGroup";
import { useRegisterStepSave } from "./useRegisterStepSave";

export interface MaterialCharacteristicsStepProps extends ProductStepProps {
  materialItemTypes?: ItemType[];
  materialProductType?: ProductType;
  formatProductSelection?: ({
    product,
    itemType,
    selectedItemsMaterialCharacteristics,
  }: {
    product: ConfigurableProduct;
    itemType: ItemType;
    selectedItemsMaterialCharacteristics: ItemMaterialCharacteristics[];
  }) => UpdateProductArgs;
}

type PersistedItem = ConfigurableProduct["items"][number];

/** This step is tooth-scoped: arch items never reach it and carry no tooth. */
const isToothScopedPersistedItem = (
  item: PersistedItem
): item is Extract<PersistedItem, { tooth: number }> =>
  item.scope === ItemScopeEnum.tooth;

const defaultFormatProductSelection = ({
  product,
  itemType,
  selectedItemsMaterialCharacteristics,
}: {
  product: ConfigurableProduct;
  itemType: ItemType;
  selectedItemsMaterialCharacteristics: ItemMaterialCharacteristics[];
}): UpdateProductArgs => {
  return {
    productId: product.id,
    items: product.items.map(item => {
      const matchingItem =
        isToothScopedItem(item) && item.type === itemType
          ? selectedItemsMaterialCharacteristics.find(
              i => i.tooth === item.tooth
            )
          : undefined;

      return {
        ...item,
        materialCharacteristics:
          matchingItem?.materialCharacteristics ?? item.materialCharacteristics,
      };
    }),
  };
};

const validateSelection = ({
  value,
}: {
  value: DeepPartial<ProductMaterialCharacteristics> | undefined;
}) => {
  const result = validateSchema(productMaterialCharacteristicsSchema, value);
  return result.success ? undefined : result.errors;
};

export const MaterialCharacteristicsStep = ({
  stepKey,
  product,
  itemType,
  orderConfiguration,
  bulkGroup,
  registerStepSave,
  formatProductSelection = defaultFormatProductSelection,
  materialItemTypes,
  materialProductType,
}: MaterialCharacteristicsStepProps) => {
  const {
    isLoading: isItemMaterialChoicesLoading,
    itemMaterials,
    itemShadeTypes,
    itemShades,
  } = useItemMaterialsByItemType({ itemType });
  const {
    isLoading: isOrderLoading,
    updateProducts,
    updateProduct,
  } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "common",
  });

  const { isBulkMode, groupProducts, groupTeeth } = useStepBulkGroup(
    product,
    bulkGroup
  );

  const persistedItems = useMemo(
    () =>
      (isBulkMode
        ? groupProducts.flatMap(groupProduct => groupProduct.items)
        : (product?.items ?? [])
      )
        .filter(isToothScopedPersistedItem)
        .filter(
          item => !materialItemTypes || materialItemTypes.includes(item.type)
        ),
    [product, isBulkMode, groupProducts, materialItemTypes]
  );

  const defaultIsPerTooth = useMemo(
    () => hasDistinctItemCharacteristics(persistedItems),
    [persistedItems]
  );

  const defaultValues = useMemo(
    () => ({
      product: buildProductMaterialCharacteristics(groupTeeth, persistedItems),
    }),
    [groupTeeth, persistedItems]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const items = getCompleteItemCharacteristics(value.product?.items);

      if (isBulkMode) {
        try {
          await updateProducts(
            groupProducts.map(groupProduct =>
              formatProductSelection({
                product: groupProduct,
                itemType,
                selectedItemsMaterialCharacteristics: items,
              })
            )
          );
        } catch {
          notifications.show({
            title: t("errorNotificationTitle"),
            message: t("errorNotificationMessage"),
            color: "red",
          });
          return;
        }

        return;
      }

      try {
        await updateProduct(
          formatProductSelection({
            product,
            itemType,
            selectedItemsMaterialCharacteristics: items,
          })
        );
      } catch {
        notifications.show({
          title: "Erreur",
          message:
            "Une erreur est survenue lors de l'enregistrement des caractéristiques. Veuillez réessayer.",
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset({ product: defaultValues.product });
    }
  }, [product, defaultValues, form]);

  const validateTooth = (
    tooth: number
  ): ValidationErrors<MaterialCharacteristics> | undefined => {
    const items = form.getFieldValue("product")?.items ?? [];
    const found = items.find(item => item?.tooth === tooth);
    const result = validateSchema(
      materialCharacteristicsSchema,
      found?.materialCharacteristics
    );
    return result.success ? undefined : result.errors;
  };

  if (isOrderLoading || isItemMaterialChoicesLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">Produit introuvable pour cette configuration.</Alert>
      </Stack>
    );
  }

  const isSolidarized = isBulkMode
    ? bulkGroup?.groupProducts.some(p =>
        SOLIDARIZED_PRODUCT_TYPE.includes(p.type)
      )
    : SOLIDARIZED_PRODUCT_TYPE.includes(product.type);

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          Caractéristiques
        </Title>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const result = validateSelection({ value });

              if (!result) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return result;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <ProductMaterialCharacteristicsForm
                value={field.state.value}
                errors={formErrors}
                onChange={next => field.setValue(next)}
                teeth={groupTeeth}
                productType={materialProductType ?? product.type}
                isSolidarized={isSolidarized}
                defaultIsPerTooth={defaultIsPerTooth}
                itemMaterials={itemMaterials}
                itemShadeTypes={itemShadeTypes}
                itemShades={itemShades}
                validateTooth={validateTooth}
                onBlur={() => field.handleBlur()}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
